import { Error } from "@Components/common/Error";
import { Loading } from "@Components/common/Loading";
import { WeatherDetailsContainer } from "@Components/forecast/WeatherDetailsContainer";
import { NavigationContainer } from "@Components/forecast/daily/NavigationContainer";
import LocationContainer from "@Components/forecast/location/LocationContainer";
import { SearchContainer } from "@Components/forecast/search/SearchContainer";
import { SENTRY_CONSENT_UC_ID } from "@Constants/consentManagement";
import {
  useForecastAppContext,
  useForecastAppDispatcher,
} from "@Context/ForecastAppContext/ForecastAppContext";
import { useConsentManagement } from "@Hooks/useConsentManagement";
import { useForecastPointWeek } from "@Hooks/useForecastPointWeek";
import { useGeolocationName } from "@Hooks/useGeolocationName";
import { useQueryParam } from "@Hooks/useQueryParam";
import { useSymbolTexts } from "@Hooks/useSymbolTexts";
import { AnalyticsStats } from "@Models/AnalyticsStats";
import { Environment } from "@Models/Environment";
import { formatAsIsoStyleDate, isValidIsoDate, toUtcDate } from "@Utils/date";
import {
  enableOrDisableSentryBasedOnConsent,
  enrichErrorsWithGeolocation,
} from "@Utils/sentry";
import { getTrackingReferrer } from "@Utils/tracking";
import { lazy, Suspense, useEffect, useState } from "react";

const ForecastDebugTools = lazy(() =>
  import("@Components/debug/ForecastDebugTools").then((module) => ({
    default: module.ForecastDebugTools,
  })),
);

type ForecastAppProps = {
  env: Environment;
  devMode: boolean;
  geolcationIdFallback?: string | null;
};

export const ForecastApp = ({
  env,
  devMode,
  geolcationIdFallback,
}: ForecastAppProps) => {
  const dispatch = useForecastAppDispatcher();
  const { env: contextEnv } = useForecastAppContext();
  const symbolTexts = useSymbolTexts();
  const { currentValue: currentDateQueryValue } = useQueryParam("date");
  const [isLoading, setIsLoading] = useState(true);
  const [analyticsStats, setAnalyticsStats] = useState<AnalyticsStats>({});

  const { initialised: sentryConsentInitialised, active: sentryConsentActive } =
    useConsentManagement(SENTRY_CONSENT_UC_ID);

  const { forecastPointWeek, error } =
    useForecastPointWeek(geolcationIdFallback);
  const geolocationName = useGeolocationName(forecastPointWeek?.geolocation);

  useEffect(() => {
    if (env !== contextEnv) {
      dispatch({
        type: "set-env",
        data: env,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    enrichErrorsWithGeolocation(forecastPointWeek?.geolocation);

    dispatch({
      type: "set-forecast-point-week",
      data: forecastPointWeek,
    });

    if (geolocationName) {
      setAnalyticsStats({
        "16": geolocationName.country || "",
        "17": forecastPointWeek?.geolocation.id,
        "44": geolocationName.name,
      });
    }
  }, [forecastPointWeek, geolocationName, dispatch]);

  useEffect(() => {
    dispatch({
      type: "set-symbol-texts",
      data: symbolTexts,
    });
  }, [symbolTexts, dispatch]);

  useEffect(() => {
    // If there is ?date in the URL, set it as the active date
    if (currentDateQueryValue && isValidIsoDate(currentDateQueryValue)) {
      dispatch({
        type: "set-active-date",
        data: {
          value: currentDateQueryValue,
          source: "initial",
        },
      });
    } else {
      // If there is no ?date in the URL, set the active date to today
      const today = toUtcDate().toISOString();
      const todayDateOnly = formatAsIsoStyleDate(today);
      dispatch({
        type: "set-active-date",
        data: {
          value: todayDateOnly,
          source: "initial",
        },
      });
    }
  }, [currentDateQueryValue, dispatch]);

  useEffect(() => {
    setIsLoading(
      typeof forecastPointWeek === "undefined" ||
        typeof symbolTexts === "undefined",
    );
  }, [forecastPointWeek, symbolTexts]);

  useEffect(() => {
    enableOrDisableSentryBasedOnConsent(
      sentryConsentInitialised,
      sentryConsentActive,
    );
  }, [sentryConsentInitialised, sentryConsentActive]);

  return (
    <div
      className="js-esi-statistics-overwrites"
      data-analytics-json={JSON.stringify(analyticsStats)}
      data-referrer-track={getTrackingReferrer({
        srg_override_mod4: "meteo_local_forecast",
      })}>
      {devMode && (
        <Suspense fallback={null}>
          <ForecastDebugTools />
        </Suspense>
      )}
      <SearchContainer />

      {error ? (
        <Error />
      ) : (
        <>
          {isLoading ? (
            <Loading />
          ) : (
            <>
              <LocationContainer />
              <NavigationContainer />
              <WeatherDetailsContainer />
            </>
          )}
        </>
      )}
    </div>
  );
};
